import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import { getUserLevel } from "../../helpers/auth";
import {
  DESKRIPSI_MENU,
  DESKRIPSI_SUBMENU,
  getProfilRole,
} from "../../data/panduanTour";

const TOUR_VERSION = "v1";
const KUNCI_TOUR = (idUser) => `asera_tour_${TOUR_VERSION}_${idUser || "umum"}`;

const bacaUser = () => {
  const auth = getUserLevel() || {};
  let name = "";
  try {
    const token = Cookies.get("access_token");
    if (token) {
      const decoded = jwtDecode(token);
      name = decoded.name || decoded.username || decoded.full_name || "";
    }
  } catch {
    // Token tidak terbaca -- tour tetap bisa jalan tanpa nama.
  }
  return { ...auth, name };
};

const ambilLangkah = (user) => {
  const elemen = Array.from(document.querySelectorAll('[data-tour-top="1"]'));
  const langkahMenu = elemen.map((el) => {
    const id = el.getAttribute("data-tour-id") || "";
    const label = el.getAttribute("data-tour-label") || el.textContent.trim();
    const subItems = Array.from(
      el.parentElement?.querySelectorAll(".sub-menu > li > a") || []
    )
      .map((a) => {
        const path = a.getAttribute("href") || "";
        const labelSub =
          a.querySelectorAll("span")[1]?.textContent.trim() ||
          a.textContent.trim();
        return {
          path,
          label: labelSub,
          isi:
            DESKRIPSI_SUBMENU[path] ||
            DESKRIPSI_SUBMENU[labelSub] ||
            `Menu ${labelSub}.`,
        };
      })
      .filter((s) => s.label);

    const unik = [];
    const sudah = new Set();
    for (const s of subItems) {
      const kunci = `${s.path}|${s.label}`;
      if (sudah.has(kunci)) continue;
      sudah.add(kunci);
      unik.push(s);
    }

    return {
      target: el,
      judul: label,
      isi: DESKRIPSI_MENU[id] || `Menu ${label}.`,
      subItems: unik,
    };
  });

  if (langkahMenu.length === 0) return [];

  const profil = getProfilRole(user.level, user.divisi);
  return [
    {
      target: null,
      judul: `Selamat datang${user.name ? `, ${user.name}` : ""}`,
      isi: `Anda masuk sebagai ${profil.label}. Tour ini hanya menampilkan menu yang terlihat oleh role Anda, termasuk fungsi setiap submenu di dalamnya. Gunakan tombol Berikutnya, atau Lewati jika sudah paham.`,
      subItems: [],
    },
    ...langkahMenu,
  ];
};

const PanduanTour = () => {
  const [aktif, setAktif] = useState(false);
  const [langkah, setLangkah] = useState([]);
  const [indeks, setIndeks] = useState(0);
  const [rect, setRect] = useState(null);
  const startedRef = useRef(false);
  const cardRef = useRef(null);
  const [tinggiKartu, setTinggiKartu] = useState(280);

  const selesai = useCallback(() => {
    const user = bacaUser();
    try {
      localStorage.setItem(KUNCI_TOUR(user.id_user), "1");
    } catch {
      // localStorage bisa diblokir; tour tetap ditutup.
    }
    setAktif(false);
    setIndeks(0);
    setRect(null);
  }, []);

  const mulai = useCallback(() => {
    const user = bacaUser();
    const daftar = ambilLangkah(user);
    if (daftar.length === 0) return;
    setLangkah(daftar);
    setIndeks(0);
    setAktif(true);
  }, []);

  // Mulai otomatis sekali per pengguna, setelah sidebar selesai dirender.
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    const user = bacaUser();
    let sudah = false;
    try {
      sudah = localStorage.getItem(KUNCI_TOUR(user.id_user)) === "1";
    } catch {
      sudah = false;
    }
    if (sudah) return;

    // Sidebar bisa belum selesai dirender saat layout pertama kali mount.
    // Coba beberapa kali sebelum menyerah.
    let percobaan = 0;
    const timer = setInterval(() => {
      percobaan += 1;
      if (document.querySelector('[data-tour-top="1"]')) {
        clearInterval(timer);
        mulai();
      } else if (percobaan >= 10) {
        clearInterval(timer);
      }
    }, 500);
    return () => clearInterval(timer);
  }, [mulai]);

  // Bisa dimulai ulang dari halaman Panduan.
  useEffect(() => {
    const handler = () => mulai();
    window.addEventListener("asera:mulai-tour", handler);
    return () => window.removeEventListener("asera:mulai-tour", handler);
  }, [mulai]);

  // Kunci scroll & tangani Escape selama tour aktif.
  useEffect(() => {
    if (!aktif) return;
    const overflowAwal = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") selesai();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = overflowAwal;
      window.removeEventListener("keydown", onKey);
    };
  }, [aktif, selesai]);

  // Ukur posisi target setiap langkah berubah / jendela berubah ukuran.
  useEffect(() => {
    if (!aktif) return;
    const item = langkah[indeks];
    if (!item) return;

    let timer;
    const ukur = () => {
      const el = item.target;
      if (!el || !document.body.contains(el)) {
        setRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) {
        setRect(null);
        return;
      }
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };

    if (item.target) {
      item.target.scrollIntoView({ block: "center", behavior: "smooth" });
      timer = setTimeout(ukur, 320);
    } else {
      setRect(null);
    }
    ukur();
    window.addEventListener("resize", ukur);
    window.addEventListener("scroll", ukur, true);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", ukur);
      window.removeEventListener("scroll", ukur, true);
    };
  }, [aktif, indeks, langkah]);

  // Ukur tinggi kartu supaya tidak keluar layar.
  useEffect(() => {
    if (!cardRef.current) return;
    setTinggiKartu(cardRef.current.offsetHeight || 280);
  }, [indeks, aktif, langkah]);

  const posisiKartu = useMemo(() => {
    const lebar = Math.min(370, window.innerWidth - 32);
    const tinggi = Math.min(tinggiKartu || 280, window.innerHeight - 32);
    if (!rect) {
      return {
        left: Math.max(16, (window.innerWidth - lebar) / 2),
        top: Math.max(16, (window.innerHeight - tinggi) / 2),
        lebar,
      };
    }
    let left;
    if (rect.left + rect.width + 16 + lebar <= window.innerWidth) {
      left = rect.left + rect.width + 16;
    } else if (rect.left - 16 - lebar >= 0) {
      left = rect.left - 16 - lebar;
    } else {
      left = Math.max(16, (window.innerWidth - lebar) / 2);
    }
    const top = Math.min(
      Math.max(16, rect.top - 12),
      window.innerHeight - tinggi - 16
    );
    return { left, top, lebar };
  }, [rect, tinggiKartu]);

  if (!aktif || langkah.length === 0) return null;

  const item = langkah[indeks];
  const terakhir = indeks === langkah.length - 1;

  const isi = createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 3000,
        fontFamily: "inherit",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Lapisan gelap + lubang sorotan */}
      {rect ? (
        <div
          style={{
            position: "fixed",
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
            borderRadius: 10,
            boxShadow: "0 0 0 9999px rgba(15, 23, 42, 0.62)",
            border: "2px solid #38bdf8",
            pointerEvents: "none",
            transition: "all 0.25s ease",
            zIndex: 3001,
          }}
        />
      ) : (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.62)",
            zIndex: 3001,
          }}
        />
      )}

      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-label="Panduan tour"
        style={{
          position: "fixed",
          left: posisiKartu.left,
          top: posisiKartu.top,
          width: posisiKartu.lebar,
          maxHeight: "calc(100vh - 32px)",
          overflowY: "auto",
          background: "#ffffff",
          borderRadius: 14,
          boxShadow: "0 24px 60px rgba(15, 23, 42, 0.35)",
          padding: "20px 22px 18px",
          zIndex: 3002,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="d-flex align-items-center justify-content-between mb-2">
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              color: "#0d6efd",
            }}
          >
            Panduan Dashboard
          </span>
          <button
            type="button"
            onClick={selesai}
            aria-label="Tutup panduan"
            style={{
              border: 0,
              background: "transparent",
              fontSize: 20,
              lineHeight: 1,
              color: "#94a3b8",
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>

        <h5 style={{ fontWeight: 700, marginBottom: 8 }}>{item.judul}</h5>
        <p style={{ color: "#475569", fontSize: 14, lineHeight: 1.6, marginBottom: 10 }}>
          {item.isi}
        </p>

        {item.subItems?.length > 0 && (
          <div className="mb-3">
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "#64748b",
                marginBottom: 6,
              }}
            >
              Submenu dan fungsinya:
            </div>
            <div style={{ maxHeight: 230, overflowY: "auto", paddingRight: 4 }}>
              {item.subItems.map((s) => (
                <div key={`${s.path}-${s.label}`} className="mb-2">
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#0f172a",
                    }}
                  >
                    {s.label}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      lineHeight: 1.5,
                      color: "#64748b",
                    }}
                  >
                    {s.isi}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="d-flex align-items-center justify-content-between mt-3">
          <small style={{ color: "#94a3b8" }}>
            Langkah {indeks + 1} dari {langkah.length}
          </small>
          <div className="d-flex gap-2">
            {indeks > 0 && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setIndeks((i) => Math.max(0, i - 1))}
              >
                Sebelumnya
              </button>
            )}
            {!terakhir && (
              <button
                type="button"
                className="btn btn-sm btn-link text-muted"
                onClick={selesai}
              >
                Lewati
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => (terakhir ? selesai() : setIndeks((i) => i + 1))}
            >
              {terakhir ? "Selesai" : "Berikutnya"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );

  return isi;
};

export default PanduanTour;
